import { formatNumber } from "accounting"
import { Dropdown, Flex, Space, Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import ButtonComponent from "../../../../../Components/Button"
import { titleSortFilters } from "../../../../../Components/titleSortFilters"
import Icon from "../../../../../Icon"
import {
  alertsColumnIconMap,
  dateFormat,
  serviceMap,
  statusMap,
  statusTextMap,
  tagMap
} from "../../../../../Utils/utils"
import { ROW_ACTIONS } from "../../constants/rowActions"

dayjs.extend(utc)

const { Paragraph, Text } = Typography

const EMPTY = "—"

const PROFILE_UPDATE_SOURCE = "profile_update"
const PROFILE_UPDATE_STATUS_LABELS = {
  pending_update: "Pending update",
  update_in_progress: "Update in progress",
  updated: "Updated",
  updated_failed: "Updated failed"
}
const PROFILE_UPDATE_NEXT_STATUSES = {
  pending_update: ["update_in_progress"],
  update_in_progress: ["updated", "updated_failed"],
  updated: [],
  updated_failed: []
}

// Receipt reuses the platform's enrollment labels; the merchant may also answer "I'm not sure".
const PREVIOUSLY_ENROLLED_LABELS = {
  ethoca: "Ethoca",
  cdrn: "CDRN",
  rdr: "RDR",
  "i’m not sure": "I’m not sure"
}

// Programs cell: CC / OI chip. Backend може віддати одне значення (`"CC"` | `"OI"`)
// або масив. Рендер підтримує обидва варіанти.
const programsBadgeMap = {
  cc: { label: "CC", color: "blue" },
  oi: { label: "OI", color: "purple" },
  CC: { label: "CC", color: "blue" },
  OI: { label: "OI", color: "purple" }
}

const renderProgramsBadge = (value) => {
  if (!value) return "—"
  const values = Array.isArray(value) ? value : [value]
  return (
    <Space size={4} wrap>
      {values.map((v) => {
        const cfg = programsBadgeMap[v] || { label: v, color: "default" }
        return (
          <Tag key={v} color={cfg.color} style={{ margin: 0 }}>
            {cfg.label}
          </Tag>
        )
      })}
    </Space>
  )
}

export const getColumns = ({ options, handleChangeStatus, onSelectRow }) => ({
  status: {
    key: "request_status",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Status",
        sorter: true,
        icon: alertsColumnIconMap["status"],
        stateData: state,
        key: "request_status"
      }),
    dataIndex: "request_status",
    fixed: "left",
    minWidth: 130,
    render: (_, record) => {
      const color = statusMap[_] || tagMap[_?.toLowerCase()] || "default"
      const isProfileUpdate = record.source === PROFILE_UPDATE_SOURCE
      const statusLabel = isProfileUpdate
        ? PROFILE_UPDATE_STATUS_LABELS[_] || _
        : statusTextMap[_] || _
      const statusOptions = isProfileUpdate ? PROFILE_UPDATE_NEXT_STATUSES[_] || [] : options || []
      const hasNextStatus = statusOptions.some((status) => status !== _)

      return (
        <Dropdown
          disabled={isProfileUpdate && !hasNextStatus}
          destroyOnHidden
          overlayClassName="custom-dropdown"
          menu={{
            items: statusOptions.map((el) => {
              if (el === _) return null
              return {
                key: el,
                label: (
                  <Flex gap={8} align="center">
                    <Text>
                      {isProfileUpdate
                        ? PROFILE_UPDATE_STATUS_LABELS[el] || el
                        : statusTextMap[el] || el}
                    </Text>
                  </Flex>
                )
              }
            }),
            onClick: ({ key }) => handleChangeStatus({ record, newStatus: key })
          }}
        >
          <Tag
            color={color}
            key={_}
            className={`simple newTagStyle ${statusMap[_]}`}
            style={{ margin: 0, color }}
            data-testid={`deflect-admin-status-${record.id}`}
          >
            <Flex gap={8} align="center">
              <Text style={{ color }}>{statusLabel}</Text>
              {(!isProfileUpdate || hasNextStatus) && (
                <Icon type="chevron_down_small" style={{ marginLeft: -4, marginRight: -4 }} />
              )}
            </Flex>
          </Tag>
        </Dropdown>
      )
    }
  },
  programs: {
    key: "programs",
    sorter: false,
    title: (state) =>
      titleSortFilters({
        title: "Programs",
        icon: alertsColumnIconMap["programs"],
        stateData: state,
        key: "programs"
      }),
    dataIndex: "programs",
    minWidth: 120,
    render: (_) => renderProgramsBadge(_)
  },
  user_full_name: {
    key: "user_full_name",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "User full name",
        sorter: true,
        icon: alertsColumnIconMap["user_full_name"],
        stateData: state,
        key: "user_full_name"
      }),
    dataIndex: "user_full_name",
    minWidth: 185,
    render: (_) => (_ ? <Text>{_}</Text> : "—")
  },
  user_email: {
    key: "user_email",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "User email",
        sorter: true,
        icon: alertsColumnIconMap["user_email"],
        stateData: state,
        key: "user_email"
      }),
    dataIndex: "user_email",
    minWidth: 185,
    render: (_) => (_ ? <Text>{_}</Text> : "—")
  },
  merchant_descriptor: {
    key: "merchant_descriptor",
    sorter: false,
    title: (state) =>
      titleSortFilters({
        title: "Billing descriptor",
        icon: alertsColumnIconMap["merchant_descriptor"],
        stateData: state,
        key: "merchant_descriptor"
      }),
    dataIndex: "merchant_descriptor",
    minWidth: 185,
    render: (list) => {
      const text = Array.isArray(list) ? list.join(", ") : list
      if (!text) return EMPTY
      return (
        <Paragraph ellipsis={{ rows: 4, expandable: "collapsible" }} copyable={{ tooltips: false }}>
          {text}
        </Paragraph>
      )
    }
  },
  enrollments: {
    key: "enrollments",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Enrollments",
        sorter: true,
        icon: alertsColumnIconMap["enrollments"],
        stateData: state,
        key: "enrollments"
      }),
    render: (_) => serviceMap[_] || _ || "—",
    dataIndex: "enrollments",
    minWidth: 150
  },
  visa_arn_code_date: {
    key: "visa_arn_code_date",
    sorter: false,
    title: (state) =>
      titleSortFilters({
        title: "ARN Added Date",
        icon: alertsColumnIconMap["visa_arn_code_date"],
        stateData: state,
        key: "visa_arn_code_date"
      }),
    dataIndex: "visa_arn_code_date",
    minWidth: 185,
    render: (_) =>
      _ ? (
        <Space direction="vertical" size={4}>
          <Text>{dayjs(_).format(dateFormat)}</Text>
          <Text type="secondary">{dayjs(_).format("h:mm A [(UTC)]")}</Text>
        </Space>
      ) : (
        "—"
      )
  },
  arn_codes: {
    key: "arn_codes",
    sorter: false,
    title: (state) =>
      titleSortFilters({
        title: "ARN codes",
        icon: alertsColumnIconMap["arn_codes"],
        stateData: state,
        key: "arn_codes"
      }),
    dataIndex: "arn_codes",
    minWidth: 240,
    render: (_) =>
      _?.length ? (
        <Paragraph ellipsis={{ rows: 4, expandable: "collapsible" }} copyable={{ tooltips: false }}>
          {_.join(", ")}
        </Paragraph>
      ) : (
        "—"
      )
  },
  bin_codes: {
    key: "bin_codes",
    sorter: false,
    title: (state) =>
      titleSortFilters({
        title: "BIN codes",
        icon: alertsColumnIconMap["bin_codes"],
        stateData: state,
        key: "bin_codes"
      }),
    dataIndex: "bin_codes",
    minWidth: 240,
    render: (_) =>
      _?.length ? (
        <Paragraph ellipsis={{ rows: 4, expandable: "collapsible" }} copyable={{ tooltips: false }}>
          {_.join(", ")}
        </Paragraph>
      ) : (
        "—"
      )
  },
  caid_codes: {
    key: "caid_codes",
    sorter: false,
    title: (state) =>
      titleSortFilters({
        title: "CAID codes",
        icon: alertsColumnIconMap["caid_codes"],
        stateData: state,
        key: "caid_codes"
      }),
    dataIndex: "caid_codes",
    minWidth: 240,
    render: (_) =>
      _?.length ? (
        <Paragraph ellipsis={{ rows: 4, expandable: "collapsible" }} copyable={{ tooltips: false }}>
          {_.join(", ")}
        </Paragraph>
      ) : (
        "—"
      )
  },
  another_solution_name: {
    key: "another_solution_name",
    sorter: false,
    title: (state) =>
      titleSortFilters({
        title: "Enrollment details",
        icon: alertsColumnIconMap["another_solution_name"],
        stateData: state,
        key: "another_solution_name"
      }),
    dataIndex: "another_solution_name",
    minWidth: 180,
    // The three competitor columns only carry meaning when the merchant said it uses another
    // solution; otherwise the cell reads as not applicable (spec BLOCK 4, columns 15-17).
    render: (_, record) => (record.is_use_another_solutions ? _ || EMPTY : EMPTY)
  },
  previously_connected_enrollments: {
    key: "previously_connected_enrollments",
    sorter: false,
    title: (state) =>
      titleSortFilters({
        title: "Previously enrolled",
        icon: alertsColumnIconMap["previously_connected_enrollments"],
        stateData: state,
        key: "previously_connected_enrollments"
      }),
    dataIndex: "previously_connected_enrollments",
    minWidth: 185,
    render: (_, record) =>
      record.is_use_another_solutions
        ? _?.map((el) => PREVIOUSLY_ENROLLED_LABELS[el] || el)?.join(", ") || EMPTY
        : EMPTY
  },
  is_user_notified_previous_solution: {
    key: "is_user_notified_previous_solution",
    sorter: false,
    title: (state) =>
      titleSortFilters({
        title: "Previous partner notified",
        icon: alertsColumnIconMap["is_user_notified_previous_solution"],
        stateData: state,
        key: "is_user_notified_previous_solution"
      }),
    dataIndex: "is_user_notified_previous_solution",
    minWidth: 215,
    // Nullable on the backend: the merchant may simply never have answered, and that is not a "No".
    render: (_, record) => {
      if (!record.is_use_another_solutions || _ === null || _ === undefined) return EMPTY

      return _ ? "Yes" : "No"
    }
  },
  balance_current: {
    key: "balance_current",
    title: (state) =>
      titleSortFilters({
        title: "Current Balance",
        icon: alertsColumnIconMap["balance_current"],
        stateData: state,
        key: "balance_current",
        tooltip: "The balance is shared across all business accounts"
      }),
    dataIndex: "balance_current",
    width: 180,
    render: (_, record) => (
      <ButtonComponent
        type="link"
        shape="circle"
        disabled={!record?.user_id}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onSelectRow(record, "user_balance")
        }}
        className="flex center"
      >
        {_ >= 0 ? "$" + formatNumber(_, 2, ",") : "-$" + formatNumber(Math.abs(_), 2, ",")}
      </ButtonComponent>
    )
  },
  is_use_another_solutions: {
    key: "is_use_another_solutions",
    sorter: false,
    title: (state) =>
      titleSortFilters({
        title: "Using Competitor",
        icon: alertsColumnIconMap["is_use_another_solutions"],
        stateData: state,
        key: "is_use_another_solutions"
      }),
    dataIndex: "is_use_another_solutions",
    minWidth: 180,
    render: (_) => (_ ? "Yes" : "No")
  },
  created_timestamp: {
    key: "created_timestamp",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Request date",
        sorter: true,
        icon: alertsColumnIconMap["created_timestamp"],
        stateData: state,
        key: "created_timestamp"
      }),
    dataIndex: "created_timestamp",
    minWidth: 185,
    render: (_) => (
      <Space direction="vertical" size={4}>
        <Text>{dayjs(_).format(dateFormat)}</Text>
        <Text type="secondary">{dayjs(_).format("h:mm A [(UTC)]")}</Text>
      </Space>
    )
  },
  updated_timestamp: {
    key: "updated_timestamp",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: "Update status date",
        sorter: true,
        icon: alertsColumnIconMap["updated_timestamp"],
        stateData: state,
        key: "updated_timestamp"
      }),
    dataIndex: "updated_timestamp",
    minWidth: 200,
    render: (_) =>
      _ ? (
        <Space direction="vertical" size={4}>
          <Text>{dayjs(_).format(dateFormat)}</Text>
          <Text type="secondary">{dayjs(_).format("h:mm A [(UTC)]")}</Text>
        </Space>
      ) : (
        "—"
      )
  }
})

export const getDefaultColumn = ({ field_id, label }) => ({
  key: field_id,
  sorter: true,
  title: (state) =>
    titleSortFilters({
      title: label,
      sorter: true,
      icon: alertsColumnIconMap[field_id],
      stateData: state,
      key: field_id
    }),
  dataIndex: field_id,
  ellipsis: true
})

export const getActionColumn = ({ onSelectRow }) => ({
  key: "actions",
  title: (state) =>
    titleSortFilters({
      title: "Actions",
      sorter: false,
      stateData: state,
      key: "delete"
    }),
  width: 176,
  render: (_, record) => {
    const handleAction = (e, data, type) => {
      e.preventDefault()
      e.stopPropagation()
      onSelectRow(data, type)
    }
    return (
      <Flex gap={4}>
        <Tooltip title="Profile details">
          <ButtonComponent
            type="link"
            shape="circle"
            icon={<Icon type="eye" />}
            onClick={(e) => handleAction(e, record, ROW_ACTIONS.PROFILE_DETAILS)}
            className="flex center"
            data-testid={`deflect-admin-eye-btn-${record.id}`}
          />
        </Tooltip>
        <Tooltip title={record?.["admin_screenshots"]?.length ? "Edit comments" : "Add comments"}>
          <ButtonComponent
            type="link"
            shape="circle"
            icon={<Icon type={record?.["admin_screenshots"]?.length ? "edit_small" : "camera"} />}
            onClick={(e) => handleAction(e, record, ROW_ACTIONS.ADD_COMMENT)}
            className="flex center"
            data-testid={`deflect-admin-comment-btn-${record.id}`}
          />
        </Tooltip>
        <Tooltip title={record?.["is_archived"] ? "Unarchive" : "Archive"}>
          <ButtonComponent
            type="link"
            shape="circle"
            icon={<Icon type={record?.["is_archived"] ? "unarchive" : "archive"} />}
            onClick={(e) => handleAction(e, record, ROW_ACTIONS.ARCHIVE)}
            className="flex center"
            data-testid={`deflect-admin-archive-btn-${record.id}`}
          />
        </Tooltip>

        <Tooltip title="Delete">
          <ButtonComponent
            danger
            type="link"
            shape="circle"
            icon={<Icon type="delete" />}
            onClick={(e) => handleAction(e, record, ROW_ACTIONS.DELETE)}
            className="flex center table-action-btn"
            data-testid={`deflect-admin-delete-btn-${record.id}`}
          />
        </Tooltip>
      </Flex>
    )
  }
})
